import { PracticeWord, SpeakingModeId, WriteSpeakState, WriteSpeakTier } from 'types/writeSpeakTypes'
import i18n from 'i18next'
import { LiveWord, wordsInText } from 'utils/liveTranscription'
import { formatDate } from '../../i18n/format'

// Pure pieces of the Writing & Speaking step, kept out of the components so
// they can be tested on their own.

const PRO_TIER_PLANS = ['maayot Pro', 'maayot Teacher', 'maayot Premium', 'School']
const STANDARD_PLANS = ['maayot Standard', 'maayot Weekly']

/** The tier the step renders for before GET writing-speaking/state answers (or if it fails). */
export const tierFromPlanName = (planName?: string | null): WriteSpeakTier => {
  if (planName && PRO_TIER_PLANS.indexOf(planName) !== -1) return 'pro'
  if (planName && STANDARD_PLANS.indexOf(planName) !== -1) return 'standard'
  return 'free'
}

/**
 * What the send area shows (plan section 03):
 * - corrected: the teacher returned it, link to /correction
 * - open: already sent, the teacher hasn't closed it, "Add to your correction"
 * - waiting: the same, for a learner who can't send any more (the free correction, a lapsed
 *   Pro), so nothing can be added and the step only says it is with the teacher
 * - pro: "Send to your teacher" plus "Save without sending"
 * - pro-capped: today's correction went to another story, "Save my practice"
 * - taster: the one free correction, "Send my free correction"
 * - locked: Free, Standard and Standard trial, "Save my answer" plus the Pro upsell
 */
export type SendVariant = 'corrected' | 'open' | 'waiting' | 'pro' | 'pro-capped' | 'taster' | 'locked'

export const resolveSendVariant = (tier: WriteSpeakTier, state: WriteSpeakState | null): SendVariant => {
  const existing = state?.existing
  if (existing) {
    if (existing.corrected || !existing.isOpen) return 'corrected'
    // Adding to an open correction is a Pro send: anyone else would get a 403.
    return tier === 'pro' ? 'open' : 'waiting'
  }
  if (tier === 'pro') return state?.dailyCap ? 'pro-capped' : 'pro'
  if (state?.tasterEligible) return 'taster'
  return 'locked'
}

/** Nothing more can be written, recorded or sent on this story. */
export const variantIsFinal = (variant: SendVariant) => variant === 'corrected' || variant === 'waiting'

/** Whether a recording can reach a teacher right now, which is when it's worth uploading. */
export const variantCanSend = (variant: SendVariant) => variant === 'pro' || variant === 'taster' || variant === 'open'

export const liveWordsFrom = (words: PracticeWord[], toTraditional?: (w: string) => string): LiveWord[] =>
  words.map((w) => {
    const forms: string[] = []
    if (w.traditional && w.traditional !== w.word) forms.push(w.traditional)
    if (toTraditional) {
      const t = toTraditional(w.word)
      if (t && t !== w.word && forms.indexOf(t) === -1) forms.push(t)
    }
    return { word: w.word, forms }
  })

/** Practice words present in the learner's answer, simplified or traditional. */
export const writtenWords = (text: string, liveWords: LiveWord[]): string[] => wordsInText(text, liveWords)

export const countCharacters = (text: string): number => (text || '').replace(/\s/g, '').length

/** "All 3 words used", but never "All 1 words used". */
export const allWordsUsedLabel = (total: number): string => i18n.t('writeSpeak:words.allUsed', { count: total })

export const formatClock = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds || 0))
  return `${Math.floor(s / 60)}:${`0${s % 60}`.slice(-2)}`
}

/** Translation keys: the label of a finished take, by speaking mode. */
export const MODE_TAKE_LABEL: Record<SpeakingModeId, string> = {
  read_writing: 'writeSpeak:modes.take.read_writing',
  free_talk: 'writeSpeak:modes.take.free_talk',
  read_passage: 'writeSpeak:modes.take.read_passage',
}

/** Translation keys: the name of each speaking mode. */
export const MODE_NAME: Record<SpeakingModeId, string> = {
  read_writing: 'writeSpeak:modes.name.read_writing',
  free_talk: 'writeSpeak:modes.name.free_talk',
  read_passage: 'writeSpeak:modes.name.read_passage',
}

export interface ZhToken {
  word: string
  pinyin?: string
  english?: string
  /** Punctuation, spaces and Latin text: shown as is, never tappable. */
  plain?: boolean
}

const HAN = /\p{Script=Han}/u

/**
 * Splits free text (the learner's own answer, a story title) into tappable
 * words: greedy longest match against the words we already know from this
 * story, falling back to single characters so every Chinese character still
 * opens the dictionary.
 */
export const segmentText = (
  text: string,
  known: Map<string, { pinyin?: string; english?: string }>,
  maxLen = 6,
): ZhToken[] => {
  const tokens: ZhToken[] = []
  const chars = Array.from(text || '')
  let i = 0
  while (i < chars.length) {
    const ch = chars[i]
    if (!HAN.test(ch)) {
      const last = tokens[tokens.length - 1]
      if (last && last.plain) last.word += ch
      else tokens.push({ word: ch, plain: true })
      i += 1
      continue
    }
    let hit = ''
    for (let len = Math.min(maxLen, chars.length - i); len >= 2; len--) {
      const candidate = chars.slice(i, i + len).join('')
      if (known.has(candidate)) {
        hit = candidate
        break
      }
    }
    const word = hit || ch
    const info = known.get(word)
    tokens.push({ word, pinyin: info?.pinyin, english: info?.english })
    i += Array.from(word).length
  }
  return tokens
}

const isParagraphMarker = (word: string) => word.includes('<p>') || word.includes('</p>')

/** The story's paragraphs, split the same way the story page (LeftStory) splits them. */
export const splitParagraphs = <T extends { word: string }>(content: T[] | undefined | null): T[][] => {
  if (!Array.isArray(content) || !content.length) return []
  const result: T[][] = [[]]
  content.forEach((wordObj, index) => {
    if (isParagraphMarker(wordObj?.word || '') && index) result.push([])
    else result[result.length - 1].push(wordObj)
  })
  return result.filter((p) => p.some((w) => (w?.word || '').replace(/<[^>]+>/g, '').trim().length > 0))
}

/** "8:00 AM" in the learner's own time zone, in the interface language. */
export const localTime = (epochMs: number): string => {
  try {
    return formatDate(epochMs, { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}
